import { renderToString } from "react-dom/server";
import { createHeaderLayoutElement } from "../component-objects/headerLayout";
import { convertHeaderComponentToElement } from "../layouts/HeaderBuilderRenderer";
import {
	HeaderConfig,
	HeaderElement,
	HeaderState,
	PublishCode,
} from "../types";

// Function that creates a React component specifically for publishing
const createPublishableHeader = (state: HeaderState) => {
	// Helper function to get icon SVG
	const getIconSVG = (iconType: string) => {
		switch (iconType) {
			case "search":
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round">
						<circle cx="11" cy="11" r="8"></circle>
						<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
					</svg>
				);
			case "heart":
			case "wishlist":
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round">
						<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
					</svg>
				);
			case "cart":
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round">
						<circle cx="9" cy="21" r="1"></circle>
						<circle cx="20" cy="21" r="1"></circle>
						<path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
					</svg>
				);
			case "user":
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round">
						<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
						<circle cx="12" cy="7" r="4"></circle>
					</svg>
				);
			case "menu":
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round">
						<line x1="3" y1="12" x2="21" y2="12"></line>
						<line x1="3" y1="6" x2="21" y2="6"></line>
						<line x1="3" y1="18" x2="21" y2="18"></line>
					</svg>
				);
			default:
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round">
						<circle cx="12" cy="12" r="10"></circle>
					</svg>
				);
		}
	};

	// Simple component renderer
	const renderComponent = (component: any) => {
		// Use simple placeholders for preview
		switch (component.type) {
			case "logo":
				return (
					<div
						key={component.id}
						className={`header-${component.type}-${
							component.id.split("-")[1]
						}`}>
						<img src={component.settings.image} alt="Logo" />
					</div>
				);
			case "searchBar":
				if (component.settings.size === "full") {
					return (
						<div
							key={component.id}
							className={`header-${component.type}-${
								component.id.split("-")[1]
							}`}>
							<form>
								<div className="search-input-container">
									<span className="search-icon">
										{getIconSVG(
											component.settings.icon || "search"
										)}
									</span>
									<input
										type="text"
										placeholder={
											component.settings.placeholder
										}
										className="search-input"
									/>
								</div>
							</form>
						</div>
					);
				} else {
					return (
						<button
							key={component.id}
							className={`header-${component.type}-${
								component.id.split("-")[1]
							} search-button`}>
							{getIconSVG(component.settings.icon || "search")}
						</button>
					);
				}
			case "navItems":
				return (
					<nav
						key={component.id}
						className={`header-${component.type}-${
							component.id.split("-")[1]
						}`}>
						{component.settings.items.map((item: any) => (
							<a
								key={item.id}
								href={item.link}
								className="nav-item">
								{item.text}
							</a>
						))}
					</nav>
				);
			case "wishlist":
			case "cart":
			case "user":
			case "menu":
				return (
					<button
						key={component.id}
						className={`header-${component.type}-${
							component.id.split("-")[1]
						} header-icon-button`}>
						{getIconSVG(component.settings.icon || component.type)}
					</button>
				);
			default:
				return (
					<div
						key={component.id}
						className={`header-${component.type}-${
							component.id.split("-")[1]
						}`}>
						Unknown Component
					</div>
				);
		}
	};

	// Function to render appropriate layout
	const renderLayout = () => {
		const { layout, components } = state;

		switch (layout) {
			case "standard":
				return (
					<div className="header-layout">
						{components.map(renderComponent)}
					</div>
				);
			case "centered":
				return (
					<div className="header-layout">
						{components.map(renderComponent)}
					</div>
				);
			case "split":
				const logo = components.find((c) => c.type === "logo");
				const searchBar = components.find(
					(c) => c.type === "searchBar"
				);
				const otherComponents = components.filter(
					(c) => c.type !== "logo" && c.type !== "searchBar"
				);

				return (
					<div className="header-layout">
						<div className="header-layout-left">
							{logo && renderComponent(logo)}
							{searchBar && renderComponent(searchBar)}
						</div>
						<div className="header-layout-right">
							{otherComponents.map(renderComponent)}
						</div>
					</div>
				);
			case "stacked":
				const logoStacked = components.find((c) => c.type === "logo");
				const otherStacked = components.filter(
					(c) => c.type !== "logo"
				);

				return (
					<div className="header-layout">
						<div className="header-layout-top">
							{logoStacked && renderComponent(logoStacked)}
						</div>
						<div className="header-layout-bottom">
							{otherStacked.map(renderComponent)}
						</div>
					</div>
				);
			default:
				return (
					<div className="header-layout">
						{components.map(renderComponent)}
					</div>
				);
		}
	};

	// Main header component
	if (state.containerType === "container") {
		return (
			<header className="header-container">
				<div className="header-inner-container">{renderLayout()}</div>
			</header>
		);
	} else {
		return <header className="header-container">{renderLayout()}</header>;
	}
};

// Generate CSS
const generateCSS = (state: HeaderState): string => {
	let cssOutput = "";

	// Add CSS reset and base styles for better consistency
	cssOutput += `/* CSS Reset and Base Styles */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  line-height: 1.5;
}

a {
  text-decoration: none;
  color: inherit;
}

button {
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  color: inherit;
}

img {
  max-width: 100%;
  height: auto;
  display: block;
}

/* Header Styles */
`;

	// Process CSS for header
	const headerStyles = { ...state.styles };

	// Add sticky styles if enabled
	cssOutput += `.header-container {\n`;

	if (state.isSticky) {
		cssOutput += `  position: sticky;\n  top: 0;\n  z-index: 1000;\n`;
	}

	// Add all header styles
	Object.entries(headerStyles).forEach(([key, value]) => {
		// Skip null/undefined values
		if (value === null || value === undefined) return;
		cssOutput += `  ${key}: ${value};\n`;
	});
	cssOutput += "}\n\n";

	// Container type styles
	if (state.containerType === "container") {
		cssOutput += `.header-inner-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}\n\n`;
	}

	// Layout specific styles
	switch (state.layout) {
		case "standard":
			cssOutput += `.header-layout {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
}\n\n`;
			break;
		case "centered":
			cssOutput += `.header-layout {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
}\n\n`;
			break;
		case "split":
			cssOutput += `.header-layout {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
}\n\n`;
			cssOutput += `.header-layout-left {
  display: flex;
  align-items: center;
  gap: 16px;
}\n\n`;
			cssOutput += `.header-layout-right {
  display: flex;
  align-items: center;
  gap: 16px;
}\n\n`;
			break;
		case "stacked":
			cssOutput += `.header-layout {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
}\n\n`;
			cssOutput += `.header-layout-top {
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 8px 0;
}\n\n`;
			cssOutput += `.header-layout-bottom {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
}\n\n`;
			break;
		default:
			cssOutput += `.header-layout {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
}\n\n`;
	}

	// Add component styles
	state.components.forEach((comp) => {
		const className = `header-${comp.type}-${comp.id.split("-")[1]}`;

		// Base component styles
		cssOutput += `.${className} {\n`;
		Object.entries(comp.styles as any).forEach(([key, value]) => {
			if (value === null || value === undefined) return;
			cssOutput += `  ${key}: ${value};\n`;
		});
		cssOutput += "}\n\n";

		// Additional CSS for search input containers and inputs
		if (comp.type === "searchBar" && comp.settings?.size === "full") {
			cssOutput += `.${className} .search-input-container {
  display: flex;
  align-items: center;
  ${comp.settings?.design ? `/* Design: ${comp.settings?.design} */` : ""}
  border: ${comp.styles?.border || "1px solid #e5e7eb"};
  border-radius: ${comp.styles?.borderRadius || "0.375rem"};
  padding: ${comp.styles?.padding || "8px 12px"};
  background: ${comp.styles?.background || "transparent"};
  box-shadow: ${comp.styles?.boxShadow || "none"};
}\n\n`;

			cssOutput += `.${className} .search-icon {
  display: flex;
  align-items: center;
  color: #9ca3af;
  margin-right: 8px;
}\n\n`;

			cssOutput += `.${className} .search-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  width: 100%;
}\n\n`;
		}

		// Add search button styles
		if (comp.type === "searchBar" && comp.settings?.size === "icon") {
			cssOutput += `.${className}.search-button {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4b5563;
  background: ${comp.styles?.background || "transparent"};
  border-radius: ${comp.styles?.borderRadius || "9999px"};
  padding: ${comp.styles?.padding || "8px"};
  width: ${comp.styles?.width || "auto"};
  height: ${comp.styles?.height || "auto"};
}\n\n`;

			cssOutput += `.${className}.search-button:hover {
  background-color: #f3f4f6;
}\n\n`;
		}

		// Icon button specific styles
		if (["wishlist", "cart", "user", "menu"].includes(comp.type)) {
			// Base icon button styles
			cssOutput += `.${className}.header-icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${comp.styles?.color || "#4b5563"};
  background: ${comp.settings?.background || "transparent"};
`;

			// Shape specific styles
			switch (comp.settings?.shape) {
				case "box":
					cssOutput += `  border-radius: 0;\n`;
					break;
				case "circle":
					cssOutput += `  border-radius: 50%;\n`;
					break;
				case "rounded":
				default:
					cssOutput += `  border-radius: ${
						comp.settings?.borderRadius || 4
					}px;\n`;
					break;
			}

			// Finish base styles
			cssOutput += `  padding: ${comp.styles?.padding || "8px"};
  transition: all 0.3s ease;
}\n\n`;

			// Add hover styles
			cssOutput += `.${className}.header-icon-button:hover {
  background-color: ${comp.settings?.hoverColor || "#f3f4f6"};
}\n\n`;

			// Add animation styles based on settings
			if (
				comp.settings?.hoverAnimation &&
				comp.settings?.hoverAnimation !== "none"
			) {
				switch (comp.settings?.hoverAnimation) {
					case "spin":
						cssOutput += `@keyframes spin-${className} {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}\n\n`;
						cssOutput += `.${className}.header-icon-button:hover svg {
  animation: spin-${className} 0.5s ease;
}\n\n`;
						break;
					case "pop":
						cssOutput += `.${className}.header-icon-button:hover {
  transform: scale(1.1);
}\n\n`;
						break;
					case "flash":
						cssOutput += `@keyframes flash-${className} {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}\n\n`;
						cssOutput += `.${className}.header-icon-button:hover {
  animation: flash-${className} 0.5s;
}\n\n`;
						break;
					case "fade":
						cssOutput += `.${className}.header-icon-button:hover {
  opacity: 0.7;
}\n\n`;
						break;
				}
			}
		}

		// Nav items specific styles
		if (comp.type === "navItems") {
			// Nav container styles
			cssOutput += `.${className} {
  display: flex;
  align-items: center;
  gap: ${comp.styles?.gap || "16px"};
}\n\n`;

			// Nav item styles
			cssOutput += `.${className} .nav-item {
  color: ${comp.styles?.color || "inherit"};
  font-weight: ${comp.settings?.fontWeight || "normal"};
  text-decoration: ${comp.settings?.textDecoration || "none"};
  font-style: ${comp.settings?.fontStyle || "normal"};
  transition: color 0.2s ease;
  padding: 4px 0;
}\n\n`;

			// Nav item hover styles
			cssOutput += `.${className} .nav-item:hover {
  color: ${comp.settings?.hoverColor || "#3b82f6"};
}\n\n`;
		}
	});

	// Add responsive utilities
	cssOutput += `/* Responsive Utilities */
@media (max-width: 768px) {
  .header-layout {
    padding: 10px;
  }
  
  .header-layout-left, .header-layout-right {
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .header-layout {
    flex-direction: column;
    padding: 8px;
  }
  
  .header-layout-right, .header-layout-left {
    width: 100%;
    justify-content: center;
    margin-top: 8px;
  }
  
  .header-inner-container {
    padding: 0 8px;
  }
}`;

	return cssOutput;
};

// Function to convert component styles to React.CSSProperties
const convertStylesToReactCSS = (
	styles: Record<string, any>
): React.CSSProperties => {
	const cssProperties: Record<string, any> = {};

	Object.entries(styles).forEach(([key, value]) => {
		if (value === null || value === undefined) return;

		// Convert kebab-case to camelCase for React style properties
		const camelCaseKey = key.replace(/-([a-z])/g, (g) =>
			g[1].toUpperCase()
		);
		cssProperties[camelCaseKey] = value;
	});

	return cssProperties as React.CSSProperties;
};

// Function to generate HeaderElement objects from components
const createHeaderElements = (components: any[]): HeaderElement[] => {
	return components.map((component) => {
		const element: HeaderElement = {
			id: component.id,
			type: component.type,
			tagName: getTagNameForComponent(component.type),
			data: { ...component.settings },
			className: `header-${component.type}-${component.id.split("-")[1]}`,
			style: convertStylesToReactCSS(component.styles),
			props: {},
		};

		// Add specific props based on component type
		switch (component.type) {
			case "logo":
				element.props = {
					src: component.settings.image,
					alt: "Logo",
				};
				break;
			case "searchBar":
				if (component.settings.size === "full") {
					element.children = [
						{
							id: `${component.id}-form`,
							type: "form",
							tagName: "form",
							data: {},
							children: [
								{
									id: `${component.id}-input-container`,
									type: "div",
									tagName: "div",
									data: {},
									className: "search-input-container",
									children: [
										{
											id: `${component.id}-icon`,
											type: "icon",
											tagName: "svg",
											data: {
												icon:
													component.settings.icon ||
													"search",
												svg: getSVGData(
													component.settings.icon ||
														"search"
												),
											},
											className: "search-icon",
										},
										{
											id: `${component.id}-input`,
											type: "input",
											tagName: "input",
											data: {},
											className: "search-input",
											props: {
												type: "text",
												placeholder:
													component.settings
														.placeholder,
											},
										},
									],
								},
							],
						},
					];
				} else {
					element.props = {
						type: "button",
					};
					element.data.icon = component.settings.icon || "search";
					element.data.svg = getSVGData(
						component.settings.icon || "search"
					);
				}
				break;
			case "navItems":
				element.children = component.settings.items.map(
					(item: any) => ({
						id: item.id,
						type: "navItem",
						tagName: "a",
						data: { text: item.text },
						className: "nav-item",
						props: {
							href: item.link,
						},
					})
				);
				break;
			case "wishlist":
			case "cart":
			case "user":
			case "menu":
				element.props = {
					type: "button",
				};
				element.data.icon = component.settings.icon || component.type;
				element.data.svg = getSVGData(
					component.settings.icon || component.type
				);
				break;
		}

		return element;
	});
};

// Helper function to get SVG data for icons
const getSVGData = (iconType: string): Record<string, any> => {
	switch (iconType) {
		case "search":
			return {
				width: 20,
				height: 20,
				viewBox: "0 0 24 24",
				fill: "none",
				stroke: "currentColor",
				strokeWidth: 1.5,
				strokeLinecap: "round",
				strokeLinejoin: "round",
				paths: [
					{ type: "circle", cx: 11, cy: 11, r: 8 },
					{ type: "line", x1: 21, y1: 21, x2: 16.65, y2: 16.65 },
				],
			};
		case "heart":
		case "wishlist":
			return {
				width: 20,
				height: 20,
				viewBox: "0 0 24 24",
				fill: "none",
				stroke: "currentColor",
				strokeWidth: 1.5,
				strokeLinecap: "round",
				strokeLinejoin: "round",
				paths: [
					{
						type: "path",
						d: "M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z",
					},
				],
			};
		case "cart":
			return {
				width: 20,
				height: 20,
				viewBox: "0 0 24 24",
				fill: "none",
				stroke: "currentColor",
				strokeWidth: 1.5,
				strokeLinecap: "round",
				strokeLinejoin: "round",
				paths: [
					{ type: "circle", cx: 9, cy: 21, r: 1 },
					{ type: "circle", cx: 20, cy: 21, r: 1 },
					{
						type: "path",
						d: "M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6",
					},
				],
			};
		case "user":
			return {
				width: 20,
				height: 20,
				viewBox: "0 0 24 24",
				fill: "none",
				stroke: "currentColor",
				strokeWidth: 1.5,
				strokeLinecap: "round",
				strokeLinejoin: "round",
				paths: [
					{
						type: "path",
						d: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2",
					},
					{ type: "circle", cx: 12, cy: 7, r: 4 },
				],
			};
		case "menu":
			return {
				width: 20,
				height: 20,
				viewBox: "0 0 24 24",
				fill: "none",
				stroke: "currentColor",
				strokeWidth: 1.5,
				strokeLinecap: "round",
				strokeLinejoin: "round",
				paths: [
					{ type: "line", x1: 3, y1: 12, x2: 21, y2: 12 },
					{ type: "line", x1: 3, y1: 6, x2: 21, y2: 6 },
					{ type: "line", x1: 3, y1: 18, x2: 21, y2: 18 },
				],
			};
		default:
			return {
				width: 20,
				height: 20,
				viewBox: "0 0 24 24",
				fill: "none",
				stroke: "currentColor",
				strokeWidth: 1.5,
				strokeLinecap: "round",
				strokeLinejoin: "round",
				paths: [{ type: "circle", cx: 12, cy: 12, r: 10 }],
			};
	}
};

// Helper function to determine the appropriate HTML tag for each component type
const getTagNameForComponent = (type: string): string => {
	switch (type) {
		case "logo":
			return "img";
		case "searchBar":
			return "div";
		case "navItems":
			return "nav";
		case "wishlist":
		case "cart":
		case "user":
		case "menu":
			return "button";
		default:
			return "div";
	}
};

// Function to generate the HeaderConfig object
export const generateHeaderConfig = (state: HeaderState): HeaderConfig => {
	// Extract styles for the header container
	const headerStyles = convertStylesToReactCSS(state.styles);

	const headerElement = createHeaderLayoutElement({
		id: "main-header-layout", // Temporary ID, will be replaced on server
		layout: state.layout,
		containerType:
			state.containerType === "container" ? "container" : "full-width",
		isSticky: state.isSticky,
		headerStyles: state.styles,
		components: state.components
			.map((component: any) =>
				convertHeaderComponentToElement(component, "desktop")
			)
			.filter(Boolean) as HeaderElement[],
		viewportSize: "desktop",
	});

	// Create the HeaderConfig object
	const headerConfig: HeaderConfig = {
		id: `header-${Date.now()}`, // Generate a temporary ID (will be replaced on server)
		name: "Custom Header",
		settings: {
			layout: state.layout,
			containerType: state.containerType,
			headerType: "full",
			backgroundColor: state.styles.backgroundColor || "#ffffff",
			padding: state.styles.padding || "0",
			height: state.styles.height || "auto",
			sticky: state.isSticky || false,
			boxShadow: state.styles.boxShadow || "none",
		},
		// style: headerStyles,
		// elements: createHeaderElements(state.components),
		elements: [headerElement],
	};

	return headerConfig;
};

// Primary function that generates publishable code using React's server-side rendering
/**
 * Updates component styles based on its settings
 * This function ensures that styles are consistent with component settings
 * @param component The component to update styles for
 * @returns Updated component with consistent styles
 */
export const updateStylesBasedOnSettings = (component: any): any => {
	// Create a deep copy of the component to avoid mutating the original
	const updatedComponent = JSON.parse(JSON.stringify(component));
	const { type, settings, styles = {} } = updatedComponent;

	// Initialize styles object if it doesn't exist
	updatedComponent.styles = styles;

	// Apply common style updates based on component type
	switch (type) {
		case "logo":
			// Handle logo size settings
			if (settings.size) {
				switch (settings.size) {
					case "small":
						updatedComponent.styles.width = "80px";
						break;
					case "medium":
						updatedComponent.styles.width = "120px";
						break;
					case "large":
						updatedComponent.styles.width = "160px";
						break;
					default:
						// If custom size, keep existing width or set default
						updatedComponent.styles.width = styles.width || "auto";
				}
			}
			break;

		case "searchBar":
			// Handle search bar size and design settings
			if (settings.size === "full") {
				// Full search bar styles
				updatedComponent.styles.width = styles.width || "100%";
				updatedComponent.styles.maxWidth = styles.maxWidth || "400px";

				// Apply design styles based on settings
				if (settings.design) {
					switch (settings.design) {
						case "minimal":
							updatedComponent.styles.border = "none";
							updatedComponent.styles.borderBottom =
								"1px solid #e5e7eb";
							updatedComponent.styles.borderRadius = "0";
							updatedComponent.styles.background = "transparent";
							break;
						case "outlined":
							updatedComponent.styles.border =
								"1px solid #e5e7eb";
							updatedComponent.styles.borderRadius = "0.375rem";
							updatedComponent.styles.background = "transparent";
							break;
						case "filled":
							updatedComponent.styles.border = "none";
							updatedComponent.styles.borderRadius = "0.375rem";
							updatedComponent.styles.background = "#f3f4f6";
							break;
					}
				}
			} else {
				// Icon-only search button
				updatedComponent.styles.width = styles.width || "40px";
				updatedComponent.styles.height = styles.height || "40px";
				updatedComponent.styles.borderRadius =
					styles.borderRadius || "9999px";
			}
			break;

		case "navItems":
			// Handle navigation items styling
			if (settings.fontWeight) {
				updatedComponent.styles.fontWeight = settings.fontWeight;
			}

			if (settings.textDecoration) {
				updatedComponent.styles.textDecoration =
					settings.textDecoration;
			}

			if (settings.fontStyle) {
				updatedComponent.styles.fontStyle = settings.fontStyle;
			}

			// Set gap between nav items
			updatedComponent.styles.gap = styles.gap || "16px";
			break;

		case "wishlist":
		case "cart":
		case "user":
		case "menu":
			// Handle icon button shape settings
			if (settings.shape) {
				switch (settings.shape) {
					case "box":
						updatedComponent.styles.borderRadius = "0";
						break;
					case "circle":
						updatedComponent.styles.borderRadius = "50%";
						updatedComponent.styles.width = styles.width || "40px";
						updatedComponent.styles.height =
							styles.height || "40px";
						updatedComponent.styles.display = "flex";
						updatedComponent.styles.alignItems = "center";
						updatedComponent.styles.justifyContent = "center";
						break;
					case "rounded":
					default:
						updatedComponent.styles.borderRadius =
							styles.borderRadius || "4px";
						break;
				}
			}

			// Handle icon button size settings
			if (settings.size) {
				switch (settings.size) {
					case "small":
						updatedComponent.styles.width = "32px";
						updatedComponent.styles.height = "32px";
						updatedComponent.styles.padding = "4px";
						break;
					case "medium":
						updatedComponent.styles.width = "40px";
						updatedComponent.styles.height = "40px";
						updatedComponent.styles.padding = "8px";
						break;
					case "large":
						updatedComponent.styles.width = "48px";
						updatedComponent.styles.height = "48px";
						updatedComponent.styles.padding = "12px";
						break;
				}
			}

			// Handle hover animation settings
			if (settings.hoverAnimation && settings.hoverAnimation !== "none") {
				// Add transition for animations
				updatedComponent.styles.transition = "all 0.3s ease";
			}

			// Set background color if specified
			if (settings.background) {
				updatedComponent.styles.background = settings.background;
			}
			break;
	}

	return updatedComponent;
};

// Function to update all components in a header state
export const updateAllComponentStyles = (state: HeaderState): HeaderState => {
	const updatedState = { ...state };
	updatedState.components = state.components.map((component) =>
		updateStylesBasedOnSettings(component)
	);
	return updatedState;
};

// You can integrate this with your existing generatePublishCode function
export const generatePublishCode = (state: HeaderState): PublishCode => {
	// Update all component styles based on their settings
	const updatedState = updateAllComponentStyles(state);

	// Create the React component
	const HeaderComponent = createPublishableHeader(updatedState);

	// Render to string using React's server-side rendering
	const htmlString = renderToString(HeaderComponent);

	// Generate CSS
	const cssString = generateCSS(updatedState);

	// Generate HeaderConfig object
	const headerConfig = generateHeaderConfig(updatedState);

	return {
		html: htmlString,
		css: cssString,
		config: headerConfig,
	};
};
